// Components/SecurityEventsTable.js
import React from 'react';
import {AlertTriangle, CheckCircle, Loader, Server, Shield, XCircle} from 'lucide-react';

const SecurityEventsTable = ({events, loading, totalEvents, currentPage, pageSize, onPageChange}) => {
    // Format datetime string
    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';

        try {
            const date = new Date(dateStr);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (e) {
            return dateStr;
        }
    };

    // Get severity badge
    const getSeverityBadge = (severity) => {
        const severityMap = {
            'low': {bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: <CheckCircle size={14}/>},
            'medium': {bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: <AlertTriangle size={14}/>},
            'high': {bgColor: 'bg-orange-100', textColor: 'text-orange-700', icon: <AlertTriangle size={14}/>},
            'critical': {bgColor: 'bg-red-100', textColor: 'text-red-700', icon: <XCircle size={14}/>}
        };

        const severityObj = severityMap[severity?.toLowerCase()] || severityMap.medium;

        return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${severityObj.bgColor} ${severityObj.textColor}`}>
                {severityObj.icon}
                {severity || 'Unknown'}
            </span>
        );
    };

    // Get alert type icon
    const getAlertTypeIcon = (alertType) => {
        const iconMap = {
            'authentication': <Shield size={16} className="text-blue-500"/>,
            'authorization': <Shield size={16} className="text-orange-500"/>,
            'brute_force': <XCircle size={16} className="text-red-500"/>,
            'data_access': <Server size={16} className="text-purple-500"/>,
            'system': <Server size={16} className="text-gray-500"/>
        };

        return iconMap[alertType] || <AlertTriangle size={16} className="text-gray-500"/>;
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalEvents / pageSize);

    // Empty state
    if (!loading && (!events || events.length === 0)) {
        return (
            <div className="text-center py-8">
                <Shield size={48} className="mx-auto text-gray-300 mb-4"/>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No security events found</h3>
                <p className="text-gray-500">
                    Try adjusting your filters or check back later.
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Timestamp
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Alert Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Severity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Source IP
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            School
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                        <tr>
                            <td colSpan={6} className="px-4 py-4 text-center">
                                <Loader size={24} className="animate-spin mx-auto text-orange-500"/>
                                <p className="mt-2 text-sm text-gray-600">Loading security events...</p>
                            </td>
                        </tr>
                    ) : !events || !Array.isArray(events) ? (
                        <tr>
                            <td colSpan={6} className="px-4 py-4 text-center">
                                <p className="text-sm text-gray-600">No security events available or invalid format</p>
                            </td>
                        </tr>
                    ) : (
                        events.map((event, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    {formatDateTime(event.timestamp)}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center">
                                        <span className="mr-2">{getAlertTypeIcon(event.alert_type)}</span>
                                        <span className="text-sm font-medium text-gray-900">
                                                {event.alert_type || 'Unknown'}
                                            </span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    {getSeverityBadge(event.severity)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    {event.source_ip || 'Unknown'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    {event.school_id || 'System'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    <div className="max-w-xs truncate">
                                        {event.description || event.message || 'No description'}
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                                <span className="font-medium">
                                    {Math.min(currentPage * pageSize, totalEvents)}
                                </span>{' '}
                                of <span className="font-medium">{totalEvents}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                 aria-label="Pagination">
                                <button
                                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                        currentPage === 1
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                         fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd"
                                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </button>

                                {/* Show page numbers */}
                                {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        // If we have 5 or fewer pages, show all pages
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        // If we're near the beginning, show first 5 pages
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        // If we're near the end, show last 5 pages
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        // Otherwise, show 2 before and 2 after the current page
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => onPageChange(pageNum)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                currentPage === pageNum
                                                    ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}

                                <button
                                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                        currentPage === totalPages
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                         fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd"
                                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecurityEventsTable;